import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import { UserRoles } from '../../api/queries';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { useRiskMatrixSetupQuery } from '../../hooks/queries/hms/useHmsQueries';
import { Routes } from '../../enums/Routes';

const K = 'createSurveyPage.riskMatrixNotice';

/** The risk matrix guide offers the way back to the form when the address carries this */
export const FROM_SURVEY_PARAM = 'fra';
export const FROM_SURVEY_VALUE = 'kartlegging';

/**
 * A work environment survey uses the risk matrix as it is when it is sent. While HR has never changed it from the
 * standard, or the employees would get no routine links, the one creating the survey is told. It does not stop them.
 * Only HR can change the risk matrix, so the others are asked to tell HR.
 */
export const RiskMatrixNotice = (): ReactElement | null => {
  const { t } = useTranslation();
  const { data: setup } = useRiskMatrixSetupQuery();
  const isHr = (useGetUserinfo().data?.userRoles ?? []).includes(UserRoles.HR_ADMIN);
  if (!setup) return null;
  const notSetUp = !setup.changed;
  if (!notSetUp && setup.missingRoutines.length === 0) return null;
  const links = setup.missingRoutines.length > 1 ? 'both' : setup.missingRoutines[0];

  return (
    <Alert data-color={notSetUp ? 'warning' : 'info'} data-size="sm">
      {notSetUp && (
        <Heading level={2} data-size="2xs">
          {t(`${K}.notSetUp.title`)}
        </Heading>
      )}
      <Paragraph>{notSetUp ? t(`${K}.notSetUp.text`) : t(`${K}.links.${links}`)}</Paragraph>
      {isHr ? (
        <Paragraph>
          <Link asChild>
            <RouterLink to={`${Routes.HRRiskMatrixFullPath}?${FROM_SURVEY_PARAM}=${FROM_SURVEY_VALUE}`}>
              {t(`${K}.setUp`)}
            </RouterLink>
          </Link>
        </Paragraph>
      ) : (
        <Paragraph>{notSetUp ? t(`${K}.askHr`) : t(`${K}.askHrLinks`)}</Paragraph>
      )}
    </Alert>
  );
};
